'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useIntl } from 'react-intl';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { useIsBot } from '@packages/tracking/src/hooks/useIsBot/useIsBot';
import { Button } from '@packages/shared/src/ui/Button/Button';
import type { HTMLAttributes } from 'react';
import type {
  AvailabilityState,
  Product,
  RatingProduct,
} from '@packages/gql/generated/shopping/graphql';
import { showAllReviewsCallToActionMessages } from './ShowAllReviewsCallToAction.messages';
import { useCustomerRatingTracking } from '../Tracking/useCustomerRatingTracking/useCustomerRatingTracking';

export type ShowAllReviewsCallToActionProps = HTMLAttributes<HTMLDivElement> & {
  sku: Product['sku'];
  availabilityState: AvailabilityState;
  reviewCount: RatingProduct['reviewCount'];
};

export const ShowAllReviewsCallToAction = ({
  sku,
  availabilityState,
  reviewCount,
  className,
  ...props
}: ShowAllReviewsCallToActionProps) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { push } = useRouter();
  const isBot = useIsBot();
  const { language, localizeUrl } = useI18n();

  const { formatMessage } = useIntl();

  const { trackShowAllReviewsCallToActionButtonClick } = useCustomerRatingTracking();

  const onClickHandler = () => {
    if (!isBot) {
      const getSearchParams = () => {
        if (availabilityState === 'UNSELECTED') {
          return searchParams && searchParams.size !== 0 ? `?${searchParams.toString()}` : '';
        }
        const newSearchParams = new URLSearchParams(searchParams?.toString());
        newSearchParams.set('sku', sku);
        return `?${newSearchParams.toString()}`;
      };

      trackShowAllReviewsCallToActionButtonClick(reviewCount.toString());

      /* 
        it is needed to add hash params as well
        they are needed for activating the ability to write a review for some tenants
      */
      push(localizeUrl(`${pathname}/rating${getSearchParams()}${window.location.hash}`, language), {
        scroll: true,
      });
    }
  };

  return (
    <div {...props} className={`mt-auto flex justify-center lg:mt-1.5 ${className || ''}`}>
      <Button onClick={onClickHandler}>
        {formatMessage(showAllReviewsCallToActionMessages.buttonText, {
          reviewCount,
        })}
      </Button>
    </div>
  );
};
